import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, TextField, Button, CircularProgress, Typography } from '@mui/material';
import useReferenceForm from '../../hooks/useReferenceForm';
import { useQueryClient } from 'react-query';

const ReferenceForm = ({ onClose, id, cvId }) => {
    const queryClient = useQueryClient();
    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm({
        defaultValues: {
            name: '',
            position: '',
            location: '',
            email: '',
            phone_number: ''
        }
    });

    const { isLoading, error, data, saveReference } = useReferenceForm(id);

    useEffect(() => {
        if (data) {
            // Map backend field names to form field names
            const mappedData = {
                name: data.name || '',
                position: data.position || '',
                location: data.location || '',
                email: data.email || '',
                phone_number: data.phone_number || ''
            };
            reset(mappedData);
        }
    }, [data, reset]);

    const handleFormSubmit = async (formData) => {
        try {
            const backendData = {
                name: formData.name,
                position: formData.position,
                location: formData.location,
                email: formData.email,
                phone_number: formData.phone_number
            };
            
            // Only include cv in the data if cvId is available
            if (cvId) {
                backendData.cv = cvId;
            }
            
            await saveReference(backendData);
            queryClient.invalidateQueries(['references', cvId ?? null]);
            
            // Reset form and close modal
            reset();
            onClose();
        } catch (err) {
            // Error is handled by the hook and displayed in the form
        }
    };

    const handleCancel = () => {
        // Reset form and close modal
        reset();
        onClose();
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Box className="space-y-4">
                {error && (
                    <Typography color="error" className="mb-4">
                        {error}
                    </Typography>
                )}

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Name"
                    {...register('name', { required: 'Name is required' })}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Position"
                    {...register('position')}
                    error={!!errors.position}
                    helperText={errors.position?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Location"
                    {...register('location')}
                    error={!!errors.location}
                    helperText={errors.location?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Email"
                    type="email"
                    {...register('email', { 
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address'
                        }
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Phone Number"
                    {...register('phone_number')}
                    error={!!errors.phone_number}
                    helperText={errors.phone_number?.message}
                />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {id ? 'Update' : 'Add'} Reference
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default ReferenceForm; 